/** Participant heartbeat message on public.*.state. */
export const PARTICIPANT_STATE_TYPE = "participant_state";

/** Хост пишет сюда «комната закрыта»; гости уже подписаны на public.#. */
export const ROOM_CLOSED_CHANNEL = "public.room.closed";
export const ROOM_CLOSED_TYPE = "conference_closed";

/** Wildcard for all public channels in the room tenant (token rule: # must be last). */
export const PARTICIPANT_STATE_SUBSCRIBE = "public.#";

/** No packet for this many ms — participant is considered offline. */
export const PARTICIPANT_STATE_STALE_MS = 3500;

/**
 * Debug heartbeat in console: URL ?fpsDebug=1 or localStorage.fpsDebug=1.
 * For myself: opt-in so normal demos stay quiet.
 */
function isFpsStateDebug() {
  try {
    if (typeof window === "undefined") return false;
    if (window.localStorage?.getItem("fpsDebug") === "1") return true;
    return new URLSearchParams(window.location.search).get("fpsDebug") === "1";
  } catch {
    return false;
  }
}

/**
 * Parses public.<role>.<participant_id>.state channel name.
 * Role and participant_id come only from the channel (ACL-bound), never from JSON.
 * @param {string} channel
 */
export function parseStateChannel(channel) {
  const parts = channel.split(".");
  if (parts.length !== 4 || parts[0] !== "public" || parts[3] !== "state") {
    return undefined;
  }
  const role = parts[1];
  const participantId = parts[2];
  if (!role || !participantId) return undefined;
  return { role, participantId };
}

/**
 * Пакет «все расходимся» в public.room.closed.
 * @param {string} conferenceId
 */
export function roomClosedPacket(conferenceId) {
  return new TextEncoder().encode(JSON.stringify({
    type: ROOM_CLOSED_TYPE,
    conference_id: conferenceId,
    sent_at: Date.now()
  }));
}

/**
 * @param {Uint8Array} payload
 * @returns {{ conferenceId: string } | undefined}
 */
export function parseRoomClosed(payload) {
  try {
    const message = JSON.parse(new TextDecoder().decode(payload));
    if (message?.type !== ROOM_CLOSED_TYPE || typeof message.conference_id !== "string") {
      return undefined;
    }
    return { conferenceId: message.conference_id };
  } catch {
    return undefined;
  }
}

/**
 * Builds a JSON heartbeat packet: alive/offline, camera, microphone.
 * Role and participant_id are not trusted from the body — only from the channel ACL.
 * @param {{ participantId: string, status: "online" | "offline", camera: boolean, microphone: boolean, speaking?: boolean, videoSource?: string, wantVideoHigh?: string }} fields
 */
export function participantStatePacket({
  participantId,
  status,
  camera,
  microphone,
  speaking = false,
  videoSource = "none",
  wantVideoHigh = ""
}) {
  return new TextEncoder().encode(JSON.stringify({
    type: PARTICIPANT_STATE_TYPE,
    participant_id: participantId,
    status,
    camera: Boolean(camera),
    microphone: Boolean(microphone),
    speaking: Boolean(speaking),
    video_source: videoSource === "camera" || videoSource === "desktop" ? videoSource : "none",
    want_video_high: typeof wantVideoHigh === "string" ? wantVideoHigh : "",
    sent_at: Date.now()
  }));
}

/**
 * Parses the JSON body of a public.*.state packet (media flags only).
 * @param {Uint8Array} payload
 */
export function parseParticipantState(payload) {
  try {
    const message = JSON.parse(new TextDecoder().decode(payload));
    if (message?.type !== PARTICIPANT_STATE_TYPE || typeof message.participant_id !== "string") {
      return undefined;
    }
    const status = message.status === "offline" ? "offline" : "online";
    return {
      participantId: message.participant_id,
      status,
      camera: Boolean(message.camera),
      microphone: Boolean(message.microphone),
      speaking: Boolean(message.speaking),
      videoSource: message.video_source === "desktop" ? "desktop"
        : message.video_source === "camera" ? "camera" : "none",
      wantVideoHigh: typeof message.want_video_high === "string" ? message.want_video_high : "",
      sentAt: Number(message.sent_at) || Date.now()
    };
  } catch {
    return undefined;
  }
}

/**
 * Publishes own state at 1 Hz and listens to public.# for remote state channels.
 */
export class ParticipantStateHub {
  constructor({
    client,
    tenant,
    channelRole,
    participantId,
    onRemoteUpdate = () => {},
    onRoomClosed = () => {},
    onWarning = console.warn
  }) {
    this.client = client;
    this.tenant = tenant;
    this.channelRole = channelRole;
    this.participantId = participantId;
    this.publishChannel = `public.${channelRole}.${participantId}.state`;
    this.onRemoteUpdate = onRemoteUpdate;
    this.onRoomClosed = onRoomClosed;
    this.onWarning = onWarning;
    this.localStatus = "online";
    this.local = { camera: false, microphone: false, speaking: false, videoSource: "none", wantVideoHigh: "" };
    /** @type {Map<string, { channelRole: string, status: string, camera: boolean, microphone: boolean, speaking: boolean, receivedAt: number }>} */
    this.remote = new Map();
    this.publishTimer = undefined;
    this.listenTask = { stopped: false };
    this.stopped = false;
  }

  /** Updates local camera/mic/speaking and sends a packet immediately. */
  setLocal(change) {
    this.local = { ...this.local, ...change };
    void this.publishNow().catch(error => this.onWarning("[conference] state publish failed", error));
  }

  /** Local status online; sends a packet immediately. */
  setOnline() {
    this.localStatus = "online";
    void this.publishNow().catch(error => this.onWarning("[conference] state publish failed", error));
  }

  /** Before leaving — offline and one last packet. */
  setOffline() {
    this.localStatus = "offline";
    this.local = { camera: false, microphone: false, speaking: false, videoSource: "none", wantVideoHigh: "" };
    void this.publishNow().catch(error => this.onWarning("[conference] state offline failed", error));
  }

  /** Starts 1 Hz publishing and a single wildcard subscription. */
  start() {
    this.stopped = false;
    this.listenTask = { stopped: false };
    this.localStatus = "online";
    this.publishTimer = window.setInterval(() => {
      void this.publishNow().catch(error => this.onWarning("[conference] state tick failed", error));
    }, 1000);
    void this.publishNow().catch(() => {});
    void this.listenAll();
  }

  /** Stops the timer and wildcard subscription. */
  stop() {
    this.stopped = true;
    this.listenTask.stopped = true;
    if (this.publishTimer) {
      window.clearInterval(this.publishTimer);
      this.publishTimer = undefined;
    }
    this.client.unsubscribe(this.tenant, PARTICIPANT_STATE_SUBSCRIBE).catch(this.onWarning);
  }

  /** Publishes own heartbeat to public.<role>.<participant_id>.state. */
  async publishNow() {
    if (!this.client || this.stopped) return;
    const packet = participantStatePacket({
      participantId: this.participantId,
      status: this.localStatus,
      camera: this.local.camera,
      microphone: this.local.microphone,
      speaking: this.local.speaking,
      videoSource: this.local.videoSource,
      wantVideoHigh: this.local.wantVideoHigh || ""
    });
    if (isFpsStateDebug()) {
      console.log("[fps-state:pub]", this.publishChannel, {
        status: this.localStatus,
        camera: this.local.camera,
        microphone: this.local.microphone,
        videoSource: this.local.videoSource
      });
    }
    await this.client.publish(this.tenant, this.publishChannel, packet);
  }

  /** Subscribes once to every public.*.state channel in the tenant. */
  async listenAll() {
    const task = this.listenTask;
    try {
      // Очередь больше: на public.# лежат heartbeat всех участников + свои.
      const queue = await this.client.subscribe(this.tenant, PARTICIPANT_STATE_SUBSCRIBE, {
        queueCapacity: 128
      });
      if (isFpsStateDebug()) {
        console.log("[fps-state:sub]", PARTICIPANT_STATE_SUBSCRIBE, "tenant=", this.tenant);
      }
      while (!task.stopped && !this.stopped) {
        const message = await queue.recv();
        if (!message || task.stopped) break;
        // Важно: newest только внутри одного channel.
        // Иначе heartbeat хоста пропадает, когда в очереди лежит свой state.
        const newestByChannel = new Map();
        newestByChannel.set(message.channel, message);
        let queued;
        while ((queued = queue.tryRecv())) {
          newestByChannel.set(queued.channel, queued);
        }
        for (const item of newestByChannel.values()) {
          this.receive(item.channel, item.payload);
        }
      }
    } catch (error) {
      if (!task.stopped && !this.stopped) {
        this.onWarning(`participant state subscription ${PARTICIPANT_STATE_SUBSCRIBE}`, error);
      }
    }
  }

  /**
   * Хост объявляет закрытие комнаты. Писать может только AT с allow_pub
   * public.room.closed (роль host).
   */
  async publishRoomClosed(conferenceId) {
    if (!this.client || this.stopped) return;
    await this.client.publish(this.tenant, ROOM_CLOSED_CHANNEL, roomClosedPacket(conferenceId));
  }

  /**
   * Handles one remote state packet.
   * Role and participant_id are taken from the channel name; body participant_id must match.
   */
  receive(channel, payload) {
    if (channel === ROOM_CLOSED_CHANNEL) {
      const closed = parseRoomClosed(payload);
      if (closed) this.onRoomClosed(closed);
      return;
    }
    const channelIdentity = parseStateChannel(channel);
    if (!channelIdentity) {
      if (isFpsStateDebug()) console.log("[fps-state:skip]", channel, "not a state channel");
      return;
    }

    const parsed = parseParticipantState(payload);
    if (!parsed || parsed.participantId !== channelIdentity.participantId) {
      if (isFpsStateDebug()) console.log("[fps-state:skip]", channel, "body mismatch");
      return;
    }
    if (parsed.participantId === this.participantId) {
      if (isFpsStateDebug()) console.log("[fps-state:echo]", channel, "own heartbeat");
      return;
    }

    if (isFpsStateDebug()) {
      console.log("[fps-state:recv]", channel, {
        status: parsed.status,
        camera: parsed.camera,
        microphone: parsed.microphone,
        videoSource: parsed.videoSource
      });
    }

    this.remote.set(channelIdentity.participantId, {
      channelRole: channelIdentity.role,
      status: parsed.status,
      camera: parsed.camera,
      microphone: parsed.microphone,
      speaking: parsed.speaking,
      videoSource: parsed.videoSource,
      wantVideoHigh: parsed.wantVideoHigh || "",
      receivedAt: Date.now()
    });
    this.onRemoteUpdate(channelIdentity.participantId);
  }

  /**
   * Remote participant state with staleness applied.
   * @param {string} participantId
   */
  remoteState(participantId) {
    const state = this.remote.get(participantId);
    if (!state) {
      return {
        status: "offline",
        stale: true,
        ageMs: null,
        channelRole: undefined,
        camera: false,
        microphone: false,
        speaking: false,
        videoSource: "none",
        wantVideoHigh: ""
      };
    }
    const ageMs = Date.now() - state.receivedAt;
    const stale = ageMs > PARTICIPANT_STATE_STALE_MS;
    return {
      channelRole: state.channelRole,
      status: stale ? "offline" : state.status,
      camera: state.camera,
      microphone: state.microphone,
      speaking: state.speaking,
      videoSource: state.videoSource || "none",
      wantVideoHigh: state.wantVideoHigh || "",
      stale,
      ageMs
    };
  }

  /** Participant is online by state heartbeat (not stale, status online). */
  participantOnline(participantId) {
    const state = this.remoteState(participantId);
    return !state.stale && state.status === "online";
  }

  /** Desktop or camera video is on per latest state. */
  participantVideoOn(participantId) {
    const state = this.remoteState(participantId);
    return !state.stale && state.camera;
  }

  /** Remote video source: camera, desktop, or none. */
  participantVideoSource(participantId) {
    const state = this.remoteState(participantId);
    if (state.stale || !state.camera) {
      return "none";
    }
    return state.videoSource === "desktop" ? "desktop" : "camera";
  }

  /** Camera is on per latest state (not stale). */
  participantCameraOn(participantId) {
    const state = this.remoteState(participantId);
    return !state.stale && state.camera;
  }

  /** Microphone is on per latest state. */
  participantMicrophoneOn(participantId) {
    const state = this.remoteState(participantId);
    return !state.stale && state.microphone;
  }

  /**
   * Кто-то из живых просит high этого участника (solo / fullscreen).
   * @param {string} targetParticipantId
   */
  someoneWantsHigh(targetParticipantId) {
    if (!targetParticipantId) return false;
    for (const participantId of this.onlineParticipantIds()) {
      const state = this.remoteState(participantId);
      if (state.wantVideoHigh === targetParticipantId) return true;
    }
    return false;
  }

  /** Private media prefix: private.<role>.<participant_id> from channel name. */
  privatePrefixFor(participantId) {
    const state = this.remoteState(participantId);
    if (!state.channelRole) return undefined;
    return `private.${state.channelRole}.${participantId}`;
  }

  /** Online remote participant ids (excluding self). */
  onlineParticipantIds() {
    const out = [];
    for (const [participantId] of this.remote) {
      if (participantId === this.participantId) continue;
      if (this.participantOnline(participantId)) out.push(participantId);
    }
    return out;
  }

  /** Snapshot for UI (remote participants only). */
  snapshot() {
    const out = {};
    for (const [participantId] of this.remote) {
      out[participantId] = this.remoteState(participantId);
    }
    return out;
  }
}
